import { memo, useCallback, useRef } from 'react';
import type { UIMessage } from 'ai';
import { useApolloClient } from '@apollo/client';
import { useOnLeaveURL } from '@/utils/use-on-leave-url';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import { useAndieChatControls } from '@/providers/andie-chat-provider';
import { AndieSuggestionsDocument } from '@/graphql/generated/andie-suggestions.generated';

export type Suggestions = {
    status: ActionResponseStatusAi;
    data: {
        label: string;
        fullMessage: string;
        action: 'sendMessage' | 'injectMessage';
    }[];
};

export const AndieChatExperience = memo(
    ({ setSuggestions }: { setSuggestions: (suggestions: Suggestions) => void }) => {
        const { isLoading } = useAndieChatControls();
        const apolloClient = useApolloClient();

        const abortRef = useRef<AbortController | null>(null);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const fetchSuggestions = useCallback(
            async (msgs: UIMessage[]) => {
                abortRef.current?.abort();
                const controller = new AbortController();
                abortRef.current = controller;

                const tail = msgs.slice(-6).map((m) => ({
                    role: m.role,
                    content:
                        m.parts
                            ?.filter((p) => p.type === 'text')
                            .map((p) => ('text' in p ? p.text : ''))
                            .join('\n') || '',
                }));

                try {
                    const { data } = await apolloClient.mutate({
                        mutation: AndieSuggestionsDocument,
                        variables: { messages: tail },
                        context: { fetchOptions: { signal: controller.signal } },
                    });

                    const ready = (data?.andieSuggestions?.suggestions ?? []).filter(
                        (s) => s?.label && s?.fullMessage,
                    );

                    setSuggestions({
                        status: ActionResponseStatusAi.COMPLETE,
                        data: ready.map((s) => ({
                            label: s.label,
                            fullMessage: s.fullMessage,
                            action: 'sendMessage' as const,
                        })),
                    });
                } catch (err) {
                    if ((err as Error).name !== 'AbortError') {
                        setSuggestions({ status: ActionResponseStatusAi.COMPLETE, data: [] });
                    }
                }
            },
            [apolloClient, setSuggestions],
        );

        // useEffect(() => {
        //     return () => {
        //         abortRef.current?.abort();
        //     };
        // }, []);

        // useEffect(() => {
        //     const wasLoading = prevIsLoadingRef.current;
        //     prevIsLoadingRef.current = isLoading;

        //     if (!wasLoading || isLoading) return;
        //     if (status === 'error') {
        //         setSuggestions({ status: ActionResponseStatusAi.COMPLETE, data: [] });
        //         return;
        //     }

        //     const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
        //     const hasTextContent = lastUserMsg?.parts?.some(
        //         (p) => p.type === 'text' && p.text && !p.text.startsWith('<hidden-message>'),
        //     );
        //     if (!hasTextContent) return;

        //     setSuggestions({ status: ActionResponseStatusAi.EXECUTING, data: [] });
        //     fetchSuggestions(messages);
        //     // eslint-disable-next-line react-hooks/exhaustive-deps
        // }, [isLoading, status]);

        useOnLeaveURL(isLoading);

        return null;
    },
);
